import { Button } from 'antd';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import Modal from 'antd/lib/modal/Modal';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { ACTIVE_THEME_NAME, IS_SEIKO_THEME } from '../../branding';
import { PageContainer } from '../common';
import classNames from 'classnames';
import './styles.less';
import { useAuthState } from '../../hooks/auth';
import { getString } from '@nackle/intl-tools';
import { useLazyQuery } from '@apollo/react-hooks';
import { TERMS_QUERY } from '../../gql/queries/TERMS_QUERY';
import { AGREEMENTS_QUERY } from '../../gql/queries/AGREEMENTS_QUERY';
import { CREATE_AGREEMENT } from '../../gql/mutations/CREATE_AGREEMENT';
import { apolloService } from '../../services';
import { PATCH_AGREEMENT } from '../../gql/mutations/PATCH_AGREEMENT';
import { useTranslations } from '../../hooks/translations';



export const TermsAndConditions = forwardRef( ( { autoDisplay = false, termsFilter = null, onClose }, ref ) => {

    const { claims } = useAuthState();
    const { locale } = useTranslations();

    const [ termQuery, { data: termsData, loading: termsLoading } ] = useLazyQuery( TERMS_QUERY, { fetchPolicy: 'network-only' } );
    const [ agreementQuery, { data: agreeData, loading: agreeLoading } ] = useLazyQuery( AGREEMENTS_QUERY, {
        variables: {
            personId: claims.person_id
        },
        fetchPolicy: 'network-only'
    } );

    const loading = useMemo( () => termsLoading || agreeLoading, [ termsLoading, agreeLoading ] );
    const [ currentTermIndex, setCurrentTermIndex ] = useState( 0 );

    const [ agreed, setAgreed ] = useState( false );
    const [ visible, setVisible ] = useState( autoDisplay );

    function setCurrentTermAgreed( term ) {
        if ( !term ) {return;}
        setAgreed( term.previouslyAgreed !== undefined ?
            term.previouslyAgreed : term.isAgreementCheckedByDefault == true );
    }

    function getTermComponent( term ) {
        return {
            header: <h3>{ term.name }</h3>,
            body: ( <>
                <p dangerouslySetInnerHTML={ { __html: term.description } }  className='description'/>
                <Checkbox defaultChecked={ term.previouslyAgreed !== undefined ? term.previouslyAgreed : term.isAgreementCheckedByDefault == true } 
                    key={ term.id } className='agreement' onChange={ e => setAgreed( e.target.checked ) }>
                    <span dangerouslySetInnerHTML={ { __html: term.agreeText } } />
                </Checkbox>
            </> )
        };
    }

    /**
     * true source of which terms should be shown
     * if `termFilter` is provided then it uses only that as the filter for which terms ot show
     * otherwise it uses publisha nd previosu agreement data for filtering
     */
    const terms = useMemo( () => {

        if ( !agreeData || !termsData || termsLoading || agreeLoading ) {return null;}

        const userId = claims.person_id;

        const userAgreements = agreeData.agreements.edges
            .map( a => a.node )
            .filter( a => userId == a.personId );

        const defaultTermFilter = t => t.isPublished && !userAgreements.some( a => a.termId == t.id ) && !( claims.locale == 'de-DE' && t.type == 'BUNCHBALL_ANON_USER' );

        const terms = termsData.terms.edges
            .map( t => t.node )
            .filter( termsFilter ?? defaultTermFilter ) //note to self. should defaultTermfilter always be in effect?
            .map( t => ( {
                ...t,
                previouslyAgreed: userAgreements.find( a => a.termId == t.id )?.agreeToTerm,
                ...( t.localization[ locale ] ?? {} )
            } ) ); 
            
        if ( terms.length == 0 ) {
            setVisible( false );
            onClose?.();
            return [];
        }

        setCurrentTermAgreed( terms[ 0 ] );

        return terms;
    }, [ agreeLoading, termsData ] );
    
    useImperativeHandle( ref, () => ( {
        openTerms: ( ) => {
            setVisible( true );
        }
    } ) );

    const [ confirmLoading, setConfirmLoading ] = useState( false );
    const modalInfo = useMemo( () => terms?.[ currentTermIndex ], [ currentTermIndex, terms ] );

    useEffect( () => {
        if ( !IS_SEIKO_THEME ) {
            termQuery();
            agreementQuery();
        }
    }, [ locale ] );

    const handleOk = () => {
        setConfirmLoading( true );

        const userId = claims.person_id;
        const existingAgreement = agreeData.agreements.edges.find( a => a.node.personId == userId && a.node.termId == terms[ currentTermIndex ].id );

        const mutation = existingAgreement ?
            {
                mutation: PATCH_AGREEMENT,
                variables: {
                    input: {
                        id: existingAgreement.node.id,
                        personId: claims.person_id, 
                        agreeToTerm: agreed
                    }
                }
            } :
            {
                mutation: CREATE_AGREEMENT,
                variables: {
                    input: {
                        termId: terms[ currentTermIndex ].id, 
                        personId: claims.person_id, 
                        agreeToTerm: agreed
                    }
                }
            };

        apolloService.mutate( mutation ).then( () => {
            if ( terms.length === currentTermIndex + 1 ) {
                setVisible( false );
                onClose?.();
            } else {
                setCurrentTermIndex( currentTermIndex + 1 );
                setCurrentTermAgreed( terms[ currentTermIndex + 1 ] );
            }

            setConfirmLoading( false );

            // update the current terms with the new selections
            if ( terms.length === currentTermIndex + 1 ) {
                termQuery();
                agreementQuery();
            }
        } ).catch( () => {
            // pretend it never happened and let user try again
            setConfirmLoading( false );
        } );
    };

    return loading || IS_SEIKO_THEME || terms?.length == 0 || !modalInfo  ? <></> :
    ( 
        <PageContainer>
            <Modal
                className={ classNames( 'terms-and-conditions', ACTIVE_THEME_NAME ) }
                title={ getTermComponent( terms[ currentTermIndex ] ).header }
                visible={ visible }
                destroyOnClose={ true }
                onOk={ handleOk }
                confirmLoading={ confirmLoading }
                onCancel={ () => {} }
                footer={ [
                    <Button
                        className={ classNames( 'learning-action', { loading: confirmLoading } ) }
                        disabled={ !agreed && modalInfo.mustAgreeToTerm  }
                        key='submit'
                        loading={ confirmLoading }
                        onClick={ handleOk }>
                        { getString( 'page.courseDetails.sections.ratings.actions.submit' ) || '?' }
                    </Button>
                ] }
                >

                { getTermComponent( terms[ currentTermIndex ] ).body }
                
            </Modal>
        </PageContainer>
    );
} );